<!-- Cancellation form -->
<app-pan-card [isPageContainer]="true" pageContainerSize="xl" header="Create Cancellation Review">
  <section class="flex flex-col gap-2">
    <div class="flex gap-4">
      <app-page-select-branch
        class="flex-1"
        label="Select Branch"
        placeholder="Select branch..."
        [autoSelectBranchIds]="autoSelectBranchIds()"
        [selected]="branch()"
        (selectedChange)="onBranchChange($event)"
      />

      <app-page-select-location
        #locationSelect
        class="flex-1"
        label="Select Location"
        [placeholder]="!branchId() ? 'Select a branch first' : ''"
        [autoSelectLocationIds]="autoSelectLocationIds()"
        [selected]="location()"
        (selectedChange)="onLocationChange($event)"
        [disabled]="!branchId()"
      />
    </div>

    <div class="flex gap-4">
      <app-pan-form-control class="flex-1">
        <app-pan-floating-label>Kiosk Organization (branch alias)</app-pan-floating-label>
        <ng-select
          class="w-full"
          [items]="branchAliasOptions()"
          [bindLabel]="'name'"
          [bindValue]="'id'"
          [searchable]="true"
          [clearable]="true"
          [disabled]="!branchId() || !locationId()"
          placeholder="Select kiosk organization..."
          [(ngModel)]="selectedBranchAliasId"
          (ngModelChange)="onBranchAliasChange($event)"
        >
          <ng-template ng-option-tmp let-item="item">
            {{ item.name }}
          </ng-template>
        </ng-select>
      </app-pan-form-control>

      <app-pan-form-control class="flex-1">
        <app-pan-floating-label>Kiosk (location alias)</app-pan-floating-label>
        <ng-select
          class="w-full"
          [items]="locationAliasOptions()"
          [bindLabel]="'name'"
          [bindValue]="'id'"
          [searchable]="true"
          [clearable]="true"
          [disabled]="!locationId() || !selectedBranchAliasId()"
          placeholder="Select kiosk..."
          [(ngModel)]="selectedKioskId"
          (ngModelChange)="onKioskChange($event)"
        >
          <ng-template ng-option-tmp let-item="item">
            {{ item.name }}
            <span class="text-sm italic"> ({{ item.branch_alias_name }})</span>
          </ng-template>
        </ng-select>
      </app-pan-form-control>
    </div>

    <div class="flex gap-4 items-end">
      <app-pan-form-control class="flex-1">
        <app-pan-floating-label>Unique ID</app-pan-floating-label>
        <input
          panInput
          type="text"
          [(ngModel)]="uniqueId"
          placeholder="Enter or generate UUID"
          [disabled]="isCreatingCancellation() || (createdCancellationId() !== undefined && createdReviewId() === undefined)"
        />
      </app-pan-form-control>

      <app-pan-button
        color="info"
        (click)="generateUuid()"
        [disabled]="isCreatingCancellation() || (createdCancellationId() !== undefined && createdReviewId() === undefined)"
      >
        Generate UUID
      </app-pan-button>
    </div>

    <div class="flex gap-4 items-center">
      <app-pan-form-control class="w-64">
        <app-pan-floating-label>Cancel Date</app-pan-floating-label>
        <mat-form-field appearance="outline" class="w-full">
          <input
            matInput
            [matDatepicker]="picker"
            [ngModel]="cancelDateDate()"
            (ngModelChange)="onDateChange($event)"
            placeholder="Choose date"
          />
          <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
          <mat-datepicker #picker></mat-datepicker>
        </mat-form-field>
      </app-pan-form-control>

      <app-pan-form-control class="w-48">
        <app-pan-floating-label>Cancel Time</app-pan-floating-label>
        <mat-form-field appearance="outline" class="w-full">
          <input matInput type="time" step="1" [ngModel]="cancelTime()" (ngModelChange)="onTimeChange($event)" />
        </mat-form-field>
      </app-pan-form-control>

      <div class="flex gap-2 items-center">
        <app-pan-button color="default" (click)="setNow()">Now</app-pan-button>
        <app-pan-button color="default" (click)="add90Seconds()">+90 seconds</app-pan-button>
      </div>
    </div>

    @if (isCancelDateTooClose()) {
      <app-pan-inline-alert type="warning" class="mt-2"> This transaction is too close to another transaction </app-pan-inline-alert>
    }

    <div class="flex gap-4 items-center">
      <app-pan-form-control class="w-48">
        <app-pan-floating-label>Theft Score</app-pan-floating-label>
        <input panInput type="number" [(ngModel)]="theftScore" min="0" />
      </app-pan-form-control>

      <app-pan-form-control class="w-56">
        <app-pan-floating-label>Theft Category</app-pan-floating-label>
        <ng-select
          [(ngModel)]="theftCategory"
          [items]="theftCategoryOptions"
          [searchable]="false"
          [clearable]="false"
          placeholder="Select category"
        >
        </ng-select>
      </app-pan-form-control>

      <label>
        <input type="checkbox" panCheckbox [(ngModel)]="faceSimilarity" />
        Face similarity
      </label>
    </div>

    <section class="mt-4">
      <app-pan-section-header title="Cancellation Items">
        <app-pan-button action color="info" (click)="addItem()">Add Item</app-pan-button>
      </app-pan-section-header>
      <div class="flex flex-col gap-3">
        @for (item of items(); let i = $index; track $index) {
          <div class="grid gap-3 grid-cols-[2fr_1fr_auto]">
            <app-pan-form-control>
              <app-pan-floating-label>Item</app-pan-floating-label>
              <input panInput type="text" [(ngModel)]="item.description" placeholder="Item name" />
            </app-pan-form-control>

            <app-pan-form-control>
              <app-pan-floating-label>Price</app-pan-floating-label>
              <input panInput type="number" step="0.01" [(ngModel)]="item.price" />
            </app-pan-form-control>

            <div class="flex items-end">
              <app-pan-button color="danger" icon="delete" (click)="removeItem(i)"></app-pan-button>
            </div>
          </div>
        }
      </div>
    </section>

    <section class="mt-4">
      <app-pan-section-header title="Recent Walk-ins"></app-pan-section-header>
      <div class="flex items-center gap-3 flex-wrap">
        <input type="file" accept="image/*" multiple (change)="onRecentWalkInsSelected($event)" />
        @if (recentWalkInFiles().length > 0) {
          <span class="text-xs text-gray-500"> Selected: {{ recentWalkInFiles().length }} file(s) </span>
        } @else {
          <span class="text-xs text-gray-500">No images selected</span>
        }
      </div>
    </section>

    <section class="mt-4">
      <app-pan-section-header title="Video (MP4)"></app-pan-section-header>
      <div class="flex items-center gap-3 flex-wrap">
        <div class="flex items-center gap-2">
          <input type="file" accept="video/mp4" (change)="onVideoSelected($event)" />
          @if (videoFile(); as v) {
            <span class="text-xs text-gray-500">Selected: {{ v.name }}</span>
          } @else {
            <span class="text-xs text-gray-500">No video selected</span>
          }
        </div>
        <app-pan-form-control class="w-48">
          <app-pan-floating-label>Offset (seconds)</app-pan-floating-label>
          <input panInput type="number" min="0" step="1" [(ngModel)]="videoOffsetSeconds" />
        </app-pan-form-control>
      </div>
    </section>

    <section class="mt-4">
      <app-pan-section-header title="Image"></app-pan-section-header>
      <div class="flex items-center gap-3">
        <div class="w-20 h-20 flex items-center justify-center border rounded bg-gray-50 overflow-hidden">
          @if (photoRows()[0]?.previewUrl; as url) {
            <img [src]="url" class="object-cover w-full h-full" alt="preview" />
          } @else {
            <span class="text-xs text-gray-500 px-2">No image</span>
          }
        </div>

        <div class="flex items-center gap-2">
          <input type="file" accept="image/*" (change)="onPhotoSelected($event, 0)" />
        </div>
      </div>
    </section>

    <section class="mt-4">
      <app-pan-section-header title="Recent Cancellations">
        <app-pan-button
          action
          color="info"
          icon="refresh"
          (click)="refreshRecentCancellations()"
          [disabled]="!branchId() || !locationId() || !selectedBranchAliasId() || !selectedKioskId() || isLoadingRecent()"
          [state]="isLoadingRecent() ? 'loading' : 'default'"
        ></app-pan-button>
      </app-pan-section-header>
      <ag-grid-angular
        #recentCancellationGrid
        [rowData]="recentCancellations()"
        [columnDefs]="recentCancellationColumns"
        [gridOptions]="recentCancellationTable"
        [pagination]="true"
        [paginationPageSize]="cancelLimit()"
        [paginationPageSizeSelector]="[5, 10, 20, 50]"
        [paginationNumberFormatter]="paginationNumberFormatter"
        (paginationChanged)="onPaginationChanged($event)"
      ></ag-grid-angular>
    </section>

    <section class="flex flex-col gap-4 mt-6">
      <div class="flex justify-end items-center gap-4">
        @if (createdCancellationId(); as cancellationId) {
          <span class="text-sm">Cancellation ID: {{ cancellationId }}</span>
        }
        <app-pan-button
          (click)="createCancellation()"
          [disabled]="
            !canCreateCancellation() ||
            isCreatingCancellation() ||
            (createdCancellationId() !== undefined && createdReviewId() === undefined)
          "
          [state]="isCreatingCancellation() ? 'loading' : 'default'"
        >
          Create Cancellation
        </app-pan-button>
      </div>

      <div class="flex justify-end items-center gap-4">
        @if (createdReviewId(); as reviewId) {
          <span class="text-sm">Review ID: {{ reviewId }}</span>
        }
        <app-pan-button
          color="primary"
          (click)="createReview()"
          [disabled]="!canCreateReview() || isCreatingReview() || isCreatingCancellation()"
          [state]="isCreatingReview() ? 'loading' : 'default'"
        >
          Create Review
        </app-pan-button>
      </div>
    </section>
  </section>
</app-pan-card>

<!-- Walkout form below -->
<app-create-walkout-review></app-create-walkout-review>
